import { DialogPopupTypes, SelectOption } from '@touche-lts/panel-ui/dist'
import { makeAutoObservable } from 'mobx'
import RootStore  from 'mobx-stores/root-store'
import { computedFn } from 'mobx-utils'
import { ShippingChronoUnit, ShippingDTO } from 'models/shipping'
import { Api } from 'services'
import { generateLocalId, isLocalId } from 'utilities'
import { validate } from 'validate.js'
import {
  ShippingsMap,
  ShippingValidationErrors
} from './store-shippings-store.types'
import { getShippingFormConstraints } from './store-shippings-store.utils'

export class StoreShippingsStore {
  private rootStore:any
  private storeApi: Api['storeApi']
  private shippingApi: Api['shippingApi']

  constructor (rootStore:any) {
    this.rootStore = rootStore
    this.storeApi = rootStore.generalStore.api.storeApi
    this.shippingApi = rootStore.generalStore.api.shippingApi
    makeAutoObservable(this)
  }

  // OBSERVABLES
  private shippings: ShippingsMap = new Map()

  public shippingToEdit: ShippingDTO = null

  private shippingValidationErrors: ShippingValidationErrors = {}

  // ACTIONS
  private setShippings = (shippings: ShippingsMap): void => {
    this.shippings = shippings
  }

  private setShipping = (shipping: ShippingDTO): void => {
    this.shippings.set(shipping.id, shipping)
  }

  private deleteShipping = (shippingId: number): void => {
    this.shippings.delete(shippingId)
  }

  private setShippingToEdit = (shipping: ShippingDTO): void => {
    this.shippingToEdit = shipping
  }

  private setShippingValidationErrors = (
    errors: ShippingValidationErrors
  ): void => {
    this.shippingValidationErrors = errors
  }

  // METHODS
  public clearStore = (): void => {
    this.setShippings(null)
    this.setShippingToEdit(null)
    this.setShippingValidationErrors({})
  }

  private handleApiShippings = (shippings: ShippingDTO[]): void => {
    this.setShippings(
      new Map(shippings.map(shipping => [shipping.id, shipping]))
    )
  }

  public onStoreShippingsSectionMount = (): void => {
    this.clearStore()
    const {
      viewStorePageStore: { store }
    } = this.rootStore
    if (!store?.shippings) return
    this.handleApiShippings(store.shippings)
  }

  private getShippingById = (shippingId: number): ShippingDTO => {
    return this.shippings.get(shippingId)
  }

  public onEditShippingClick = (shippingId: number): void => {
    const shipping = this.getShippingById(shippingId)
    if (!shipping) {
      this.rootStore.generalStore.showError('Shipping option not found')
      return
    }

    if (this.isShippingEditModeActive) {
      const {
        generalStore: { openDialogPopup, closeDialogPopup }
      } = this.rootStore
      openDialogPopup({
        type: DialogPopupTypes.WARNING,
        message:
          'The changes to the shipping option you are currently editing will be lost. Are you sure you want to proceed?',
        closeButtonLabel: 'No',
        buttons: [
          {
            children: 'Yes',
            preset: 'primary',
            onClick: () => {
              this.onCancelEditingClick()
              this.setShippingToEdit(shipping)
              closeDialogPopup()
            }
          }
        ]
      })
      return
    }

    this.setShippingToEdit(shipping)
  }

  public onCancelEditingClick = (): void => {
    const shippingId = this.shippingToEdit?.id
    if (isLocalId(shippingId)) {
      this.deleteShipping(shippingId)
    }
    this.setShippingValidationErrors({})
    this.setShippingToEdit(null)
  }

  public onSaveEditingClick = async (): Promise<void> => {
    const validationErrors = validate(
      {
        ...this.shippingToEdit,
        price: this.shippingToEdit.freeShipping
          ? undefined
          : this.shippingToEdit.price,
        freeAmount: this.shippingToEdit.freeShippingAbove
          ? this.shippingToEdit.freeAmount
          : undefined
      },
      getShippingFormConstraints({
        freeShipping: this.shippingToEdit.freeShipping,
        freeShippingAbove: this.shippingToEdit.freeShippingAbove
      })
    )
    if (validationErrors) {
      this.setShippingValidationErrors(validationErrors)
      return
    }

    const shippingPayload = {
      ...this.shippingToEdit,
      price: Number(this.shippingToEdit.price),
      freeAmount: Number(this.shippingToEdit.freeAmount)
    }

    if (isLocalId(this.shippingToEdit.id)) {
      shippingPayload.id = null
      const shippings = await this.storeApi.addShipping(
        this.storeId,
        shippingPayload
      )
      if (!shippings) return
      this.handleApiShippings(shippings)
      this.setShippingToEdit(null)
    } else {
      const shipping = await this.shippingApi.updateShipping(shippingPayload)
      if (!shipping) return
      this.setShipping(shipping)
      this.setShippingToEdit(null)
      this.setShippingValidationErrors({})
    }
  }

  public onRemoveShippingClick = (shippingId: number): void => {
    const {
      generalStore: { openDialogPopup, closeDialogPopup, showError }
    } = this.rootStore
    const shipping = this.getShippingById(shippingId)
    if (!shipping) {
      showError('Shipping option not found')
      return
    }
    openDialogPopup({
      type: DialogPopupTypes.WARNING,
      message: `Are you sure you want to remove "${shipping.name}" shipping option?`,
      closeButtonLabel: 'No',
      buttons: [
        {
          children: 'Yes',
          preset: 'primary',
          onClick: async (): Promise<void> => {
            const shippings = await this.storeApi.removeShipping(
              this.storeId,
              shippingId
            )
            closeDialogPopup()
            if (!shippings) return
            this.handleApiShippings(shippings)
          }
        }
      ]
    })
  }

  public setShippingToEditProps = (props: Partial<ShippingDTO>): void => {
    const validationErrorCleared = {}
    Object.keys(props).forEach(key => {
      validationErrorCleared[key] = undefined
    })

    this.setShippingValidationErrors({
      ...this.shippingValidationErrors,
      ...validationErrorCleared
    })
    this.setShippingToEdit({ ...this.shippingToEdit, ...props })
  }

  public onCountryAdd = (country: string): void => {
    const countryAlreadyAdded = this.shippingToEditCountriesSet.has(country)
    if (countryAlreadyAdded) {
      this.rootStore.generalStore.showError('Country already added')
      return
    }

    const countries = [...(this.shippingToEdit?.countries || [])]
    countries.push(country)
    this.setShippingToEditProps({ countries })
  }

  public onCountryRemove = (country: string): void => {
    // console.log('country', country)
    // console.log('this.shippingToEditCountriesSet', this.shippingToEditCountriesSet)
    // console.log('this.shippingToEditCountriesSet.has(country)', this.shippingToEditCountriesSet.has(country))
    const countryNotFound = !this.shippingToEditCountriesSet.has(country)
    if (countryNotFound) {
      this.rootStore.generalStore.showError('Country not found')
      return
    }
    const countries = this.shippingToEdit.countries.filter(c => c !== country)
    this.setShippingToEditProps({ countries })
  }

  public onAddShippingClick = (): void => {
    const newShipping: ShippingDTO = {
      chronoUnit: ShippingChronoUnit.DAYS,
      countries: [],
      currency: 'USD',
      deliveryTime: '',
      freeAmount: '' as any,
      freeShipping: false,
      freeShippingAbove: false,
      id: generateLocalId(),
      name: '',
      price: '' as any
    }

    this.setShipping(newShipping)
    this.setShippingToEdit(newShipping)
  }

  // COMPUTED
  public get shippingsList (): ShippingDTO[] {
    return Array.from(this.shippings.values())
  }

  public get hasShippings (): boolean {
    return Boolean(this.shippingsList?.length)
  }

  public get shippingChronoUnitsAsSelectOptions (): SelectOption<
    ShippingChronoUnit
  >[] {
    return Object.values(ShippingChronoUnit).map(unit => ({
      label: unit.toLowerCase(),
      value: unit
    }))
  }

  private get shippingToEditCountriesSet (): Set<string> {
    if (!this.shippingToEdit?.countries?.length) return new Set()
    return new Set(this.shippingToEdit.countries)
  }

  public isShippingInEditMode = computedFn((shippingId: number): boolean => {
    return this.shippingToEdit?.id === shippingId
  })

  public get isShippingEditModeActive (): boolean {
    return Boolean(this.shippingToEdit)
  }

  private get storeId (): number {
    return this.rootStore.viewStorePageStore.storeId
  }

  public getErrorMessage = computedFn((key: keyof ShippingDTO): string => {
    return this.shippingValidationErrors?.[key]?.[0]
  })
}
